import type { ReactNode } from "react";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { MissingFigure, showValue } from "@/profitability/missing-value";
import { ActualVsEstimate } from "@/profitability/records/estimate-variance";
import {
  rowVariance,
  withoutShownEstimates,
} from "@/profitability/records/estimates";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import {
  findReferenced,
  referenceName,
  referenceRef,
  rowMargin,
} from "@/profitability/records/objects";
import { RecordChip } from "@/profitability/records/record-chip";
import { formatCell, isNumeric } from "@/profitability/records/table-values";
import { rate } from "@/profitability/review/labels";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { SourceButton } from "@/profitability/sources/source-button";
import { isSourced } from "@/profitability/sources/sourced";
import type { ReportColumn } from "@/profitability/types";
import { type FigureGaps, needsOf } from "./figure-gaps";
import { isAbsent } from "./missing-fields";

const MAX = 6;

const Highlight = ({
  label,
  wide = false,
  source,
  children,
}: {
  label: string;
  /** Takes the whole row: an actual beside its estimate needs the room. */
  wide?: boolean;
  /** Its "Source" button, beside the label. */
  source?: ReactNode;
  children: ReactNode;
}) => (
  <div
    className={cn("group/source flex min-w-0 flex-col gap-1", {
      "col-span-2": wide,
    })}
  >
    <div className="flex min-w-0 items-center gap-1 text-sm text-subtle">
      <span className="truncate">{label}</span>
      {source}
    </div>
    <div
      className={cn("min-w-0 text-base font-medium text-ink tabular-nums", {
        truncate: !wide,
      })}
    >
      {children}
    </div>
  </div>
);

/**
 * The few attributes that say the most about a record: the ones its
 * script lists (`object.detail.highlights`), in its order; failing that,
 * what it brought in, its rate under the report's own name for it, what it
 * is linked to and when.
 */
export const RecordHighlights = ({
  object,
  objects,
  row,
  onOpenRecord,
  gaps,
  onShowSource,
}: {
  object: RecordObject;
  objects: RecordObject[];
  row: Record<string, string | number | boolean | null>;
  onOpenRecord: (ref: RecordRef) => void;
  /** The missing figures that wait on others, said beside their "Missing". */
  gaps?: FigureGaps | null;
  /** Opens the file a figure was read from; its button only on a sourced one. */
  onShowSource?: (column: string) => void;
}) => {
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const { dataset } = object;
  const keys = dataset.key_columns ?? [];
  const declared = object.detail?.highlights
    ?.map((key) => dataset.columns.find((column) => column.key === key))
    .filter((column): column is ReportColumn => !!column);
  const margin = declared ? null : rowMargin(dataset, row);
  // The script's own margin column says what the margin card already does.
  const sameAsMargin = (column: ReportColumn) =>
    column.type === "percent" &&
    margin !== null &&
    Math.abs(Number(row[column.key]) - margin) < 0.0005;
  // An estimate shows beside its actual, so it is not a highlight of its own.
  const usable = withoutShownEstimates(
    dataset.columns.filter(
      (column) =>
        !keys.includes(column.key) && !column.flag && !sameAsMargin(column),
    ),
  );
  const estimated = (column: ReportColumn) =>
    isNumeric(column) ? rowVariance(dataset.columns, column, row) : null;
  const figure = (column: ReportColumn) => {
    const pair = estimated(column);
    return pair ? (
      <ActualVsEstimate
        variance={pair.variance}
        column={column}
        currency={currency}
      />
    ) : isNumeric(column) && isAbsent(dataset, row, column) ? (
      <MissingFigure
        source={column.source ?? null}
        needs={needsOf(gaps, column.key)}
      />
    ) : (
      showValue(formatCell(row[column.key], column, currency)) || "—"
    );
  };
  const rank = (column: ReportColumn) =>
    column.role === "total"
      ? 0
      : column.references
        ? 2
        : column.role === "result"
          ? 3
          : column.type === "date"
            ? 4
            : column.role
              ? 6
              : 5;
  const chosen =
    declared ?? [...usable].sort((a, b) => rank(a) - rank(b)).slice(0, MAX - 1);
  const source = (column: ReportColumn) =>
    onShowSource && isSourced(object.dataset, column) ? (
      <SourceButton
        label={`Show where ${column.label ?? column.key} comes from`}
        onClick={() => onShowSource(column.key)}
        className="-my-1"
      />
    ) : null;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
      {chosen.slice(0, 1).map((column) => (
        <Highlight
          key={column.key}
          label={column.label ?? column.key}
          wide={!!estimated(column)}
          source={source(column)}
        >
          {figure(column)}
        </Highlight>
      ))}
      {margin !== null && (
        <Highlight label={spec.metric.label}>{rate(margin)}</Highlight>
      )}
      {chosen.slice(1).map((column) => {
        const target = column.references
          ? objects.find((item) => item.dataset.id === column.references)
          : undefined;
        const value = row[column.key];
        return (
          <Highlight
            key={column.key}
            label={column.label ?? column.key}
            wide={!target && !!estimated(column)}
            source={source(column)}
          >
            {target && value ? (
              <RecordChip
                object={target}
                name={referenceName(target, value)}
                row={findReferenced(target, value)}
                onOpen={() => onOpenRecord(referenceRef(target, value))}
              />
            ) : (
              figure(column)
            )}
          </Highlight>
        );
      })}
    </div>
  );
};
